'use client';

import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { cn } from '../cn';
import { MOBILE_HEADER_LOGO_SRC } from '../assets';
import {
  mergeProfileHeaderBasicName,
  toHeaderProfileBasic,
} from '../profile-header';
import { useShell } from '../provider';
import type { HeaderProfileBasic } from '../types';
import { DeferredAuthModal } from '../auth/deferred-auth-modal';
import { HeaderAuthTrigger } from './header-auth-trigger';
import { HeaderMobileSearch } from './header-mobile-search';
import { isMenuItemActive } from './default-config';
import { LazyMobileNav } from '../nav/lazy-mobile-nav';
import { MenuNavLink } from '../nav/menu-nav-link';
import AdministratorMaleIcon from '../icons/administrator-male-icon';
import { DownloadAppButton } from './download-app-button';
import { HeaderNotificationController } from './header-notification-controller';
import type { HeaderNotificationItem } from './notification-types';

export type {
  HeaderNotificationGroup,
  HeaderNotificationItem,
} from './notification-types';

const AuthenticatedUserMenu = dynamic(() =>
  import('../auth/authenticated-user-menu').then(
    module => module.AuthenticatedUserMenu
  )
);

export type RootHeaderProps = {
  isLoggedIn: boolean;
  hiddenAuthModal?: boolean;
  initialProfile?: HeaderProfileBasic | null;
  initialNotifications?: HeaderNotificationItem[];
  sessionUserId?: string;
  /** Authenticated jobseeker UUID, as returned by the shell session helper. */
  authUserId?: string;
  mobileNav?: ReactNode;
};

function RootHeaderContent({
  isLoggedIn,
  hiddenAuthModal,
  initialProfile,
  initialNotifications,
  authUserId,
  sessionUserId,
  mobileNav,
}: RootHeaderProps) {
  const { config, analytics, navigation, profile } = useShell();
  const [headerProfileData, setProfileData] =
    useState<HeaderProfileBasic | null>(initialProfile ?? null);
  const profileData = isLoggedIn ? headerProfileData : null;
  const pathname = usePathname();
  const homeHref = config.homeHref;
  const profileUpdatedEventName = config.profileUpdatedEventName;
  const userIdCookieName = config.cookieNames.userId;
  const showEmployerLink =
    Boolean(config.employerLink) &&
    !(config.employerLink?.hideWhenLoggedIn && isLoggedIn);

  const updateProfileData = useCallback(
    (
      getNextProfile: (
        currentProfile: HeaderProfileBasic | null
      ) => HeaderProfileBasic | null
    ) => {
      setProfileData(currentProfile => getNextProfile(currentProfile));
    },
    []
  );

  useEffect(() => {
    if (!isLoggedIn || !profile?.getHeaderProfile) {
      return;
    }

    const controller = new AbortController();

    void profile.getHeaderProfile(controller.signal).then(nextProfileData => {
      if (!controller.signal.aborted && nextProfileData) {
        updateProfileData(() => nextProfileData);
      }
    });

    return () => {
      controller.abort();
    };
  }, [isLoggedIn, profile, updateProfileData]);

  useEffect(() => {
    const handleProfileUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ basic?: HeaderProfileBasic }>)
        .detail;

      if (!detail?.basic) return;

      updateProfileData(currentProfile => {
        const nextProfile = toHeaderProfileBasic(detail.basic);

        if (!currentProfile || !nextProfile) {
          return nextProfile;
        }

        return {
          ...mergeProfileHeaderBasicName(currentProfile, nextProfile),
          avatar: nextProfile.avatar ?? currentProfile.avatar,
        };
      });
    };

    window.addEventListener(profileUpdatedEventName, handleProfileUpdated);

    return () =>
      window.removeEventListener(profileUpdatedEventName, handleProfileUpdated);
  }, [profileUpdatedEventName, updateProfileData]);

  const renderTitle = () => {
    if (!isLoggedIn) {
      return 'Đăng nhập';
    }

    if (profileData?.lastName || profileData?.firstName) {
      return `${profileData.lastName ?? ''} ${profileData.firstName ?? ''}`.trim();
    }

    if (profileData?.email) {
      return profileData.email;
    }

    return 'Tài khoản';
  };

  const handleEmployerPageClick = () => {
    const userIdFromCookie =
      document.cookie
        .split('; ')
        .find(row => row.startsWith(`${userIdCookieName}=`))
        ?.split('=')[1] ?? null;

    if (userIdFromCookie) {
      analytics.identify(userIdFromCookie);
    }

    analytics.track('js_click_employer_page', {
      user_id: userIdFromCookie,
    });
  };

  const desktopAuthTrigger = (
    <HeaderAuthTrigger
      isLoggedIn={isLoggedIn}
      avatar={profileData?.avatar}
      label={renderTitle()}
    />
  );

  return (
    <header className="border-outline-variant border-b bg-white">
      <div className="max-w-8xl mx-auto flex h-[84px] w-full items-center px-6 xl:h-[69px] xl:px-8">
        <Link
          href={homeHref}
          aria-label={config.homeAriaLabel ?? 'Upzi - Trang chủ'}
          className="relative block h-10 w-[82px] shrink-0 overflow-hidden xl:w-[184px]"
        >
          <Image
            alt={config.logo.alt}
            className="scale-[1.18] object-contain xl:hidden"
            fill
            sizes="82px"
            fetchPriority="high"
            unoptimized
            src={MOBILE_HEADER_LOGO_SRC}
          />
          <Image
            alt={config.logo.alt}
            className="hidden object-cover object-center xl:block"
            fill
            sizes="184px"
            fetchPriority="high"
            loader={config.imageLoader}
            quality={80}
            src={config.logo.src}
          />
        </Link>
        <nav
          aria-label="Điều hướng chính"
          className="ml-8 hidden h-10 items-center xl:flex 2xl:ml-[100px]"
        >
          <div className="text-on-surface flex h-10 shrink-0 items-center gap-1 2xl:gap-5">
            {config.menu.map(item => {
              const active = navigation?.isMenuItemActive
                ? navigation.isMenuItemActive(item, pathname)
                : isMenuItemActive(item, pathname);

              return (
                <MenuNavLink
                  key={item.text}
                  item={item}
                  pathname={pathname}
                  ariaCurrent={active ? 'page' : undefined}
                  className={cn(
                    'font-body hover:bg-primary-variant hover:text-primary focus-visible:ring-primary/50 flex h-10 items-center justify-center gap-2 rounded-[14px] px-2.5 text-base leading-5 font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
                    active && 'bg-primary-variant text-primary font-semibold'
                  )}
                >
                  {item.icon ? (
                    <item.icon
                      aria-hidden
                      size={20}
                      className="shrink-0"
                      variant={active ? 'filled' : 'outline'}
                    />
                  ) : null}
                  {item.text}
                </MenuNavLink>
              );
            })}
            <DownloadAppButton />
          </div>
        </nav>
        {!hiddenAuthModal && (
          <div className="text-on-surface ml-auto flex h-full w-auto shrink-0 items-center gap-3">
            <HeaderMobileSearch />
            {showEmployerLink && config.employerLink ? (
              <a
                href={config.employerLink.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={config.employerLink.label}
                onClick={handleEmployerPageClick}
                className="bg-surface-variant hover:bg-primary-variant focus-visible:ring-primary/50 hidden h-11 items-center gap-2 rounded-2xl px-3 text-base leading-5 font-semibold focus-visible:ring-2 focus-visible:outline-none xl:flex"
              >
                <AdministratorMaleIcon size={24} aria-hidden />
                <span className="flex flex-col leading-[18px]">
                  <span className="text-[11px] leading-4">
                    {config.employerLink.eyebrow}
                  </span>
                  <span>{config.employerLink.title}</span>
                </span>
              </a>
            ) : null}
            {isLoggedIn ? (
              <HeaderNotificationController
                initialNotifications={initialNotifications}
                userId={authUserId ?? sessionUserId}
              />
            ) : null}
            {mobileNav ? (
              <div className="block xl:hidden">{mobileNav}</div>
            ) : (
              <div className="block xl:hidden">
                <LazyMobileNav isLoggedIn={isLoggedIn} basic={profileData} />
              </div>
            )}
            {isLoggedIn ? (
              <AuthenticatedUserMenu
                triggerButton={desktopAuthTrigger}
                profileData={profileData}
              />
            ) : (
              <DeferredAuthModal triggerButton={desktopAuthTrigger} />
            )}
          </div>
        )}
      </div>
    </header>
  );
}

export default function RootHeader(props: RootHeaderProps) {
  const sessionKey = props.isLoggedIn
    ? (props.authUserId ?? props.sessionUserId ?? 'authenticated')
    : 'anonymous';

  return <RootHeaderContent key={sessionKey} {...props} />;
}
